import {
	faCheck,
	faChevronDown,
	faSpinner,
	faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Combobox as HeadlessCombobox, Transition } from '@headlessui/react';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

export interface ComboboxOption {
	value: string | number;
	label: string;
	subtitle?: string;
	disabled?: boolean;
}

interface ComboboxProps {
	value?: string | number | null;
	onChange?: (value: string) => void;
	onSearch: (query: string) => Promise<ComboboxOption[]>;
	placeholder?: string;
	error?: boolean;
	fullWidth?: boolean;
	disabled?: boolean;
	className?: string;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	debounceMs?: number;
	initialOptions?: ComboboxOption[];
	emptyMessage?: string;
	clearable?: boolean;
}

export const Combobox = React.forwardRef<HTMLInputElement, ComboboxProps>(
	(
		{
			value,
			onChange,
			onSearch,
			placeholder = 'Search...',
			error = false,
			fullWidth = true,
			disabled = false,
			className,
			size = 'md',
			debounceMs = 300,
			initialOptions = [],
			emptyMessage = 'No results found',
			clearable = false,
			...props
		},
		ref,
	) => {
		const colors = useThemeColors();
		const [query, setQuery] = useState('');
		const [options, setOptions] = useState<ComboboxOption[]>(initialOptions);
		const [isLoading, setIsLoading] = useState(false);
		const [selectedOption, setSelectedOption] = useState<ComboboxOption | null>(
			null,
		);
		const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
		const latestQueryRef = useRef<string>('');

		// Update options when initialOptions changes
		useEffect(() => {
			if (!query.trim()) {
				setOptions(initialOptions);
			}
		}, [initialOptions, query]);

		// Size-based styling
		const sizeClasses = {
			xs: 'px-1.5 py-2 text-xs',
			sm: 'px-2.5 py-1.5 text-sm',
			md: 'px-3 py-2 text-sm',
			lg: 'px-4 py-2.5 text-base',
		}[size];

		// Find selected option when value changes
		useEffect(() => {
			if (value != null) {
				const option = options.find(
					(opt) => String(opt.value) === String(value),
				);

				if (option) {
					setSelectedOption(option);
					setQuery('');
				} else if (initialOptions.length > 0) {
					// If value exists but not in current options, it might be a pre-selected value
					const initialOption = initialOptions.find(
						(opt) => String(opt.value) === String(value),
					);

					if (initialOption) {
						setSelectedOption(initialOption);
						setOptions([
							initialOption,
							...options.filter((opt) => opt.value !== initialOption.value),
						]);
					}
				}
			} else {
				setSelectedOption(null);
			}
		}, [value, initialOptions]);

		const handleSearch = async (searchQuery: string) => {
			setQuery(searchQuery);
			latestQueryRef.current = searchQuery;

			// Clear previous timer
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}

			if (!searchQuery.trim()) {
				setIsLoading(false);
				setOptions(initialOptions);
				return;
			}

			// Debounce the API search
			debounceTimerRef.current = setTimeout(async () => {
				setIsLoading(true);

				try {
					const results = await onSearch(searchQuery);

					// Discard stale response if the query changed while awaiting
					if (latestQueryRef.current !== searchQuery) {
						return;
					}

					setOptions(results);
				} catch (error) {
					if (latestQueryRef.current !== searchQuery) {
						return;
					}

					setOptions([]);
				} finally {
					if (latestQueryRef.current === searchQuery) {
						setIsLoading(false);
					}
				}
			}, debounceMs);
		};

		const handleChange = (option: ComboboxOption | null) => {
			if (option) {
				setSelectedOption(option);
				onChange?.(String(option.value));
			}
		};

		return (
			<HeadlessCombobox
				value={selectedOption}
				onChange={handleChange}
				disabled={disabled}
			>
				<div className={cn('relative', fullWidth ? 'w-full' : 'w-auto')}>
					<HeadlessCombobox.Button as="div" className="relative">
						<HeadlessCombobox.Input
							ref={ref}
							className={cn(
								// Base styling
								'relative w-full text-left cursor-pointer',

								// Size-based padding and text
								sizeClasses,

								// Right padding for icon/clear button (must come after sizeClasses to override px-*)
								clearable && value != null ? 'pr-16' : 'pr-10',

								// Use form theme classes
								colors.classes.formFieldBase,

								// State-based styling
								error
									? colors.classes.formFieldError
									: colors.classes.formFieldDefault,

								// Focus state
								colors.classes.formFieldFocus,

								disabled && colors.classes.formFieldDisabled,

								fullWidth && 'w-full',
								className,
							)}
							placeholder={placeholder}
							displayValue={(option) => {
								// HeadlessUI passes the selected value here
								if (option && typeof option === 'object' && 'label' in option) {
									return String(option.label);
								}
								return '';
							}}
							onChange={(event) => handleSearch(event.target.value)}
							onKeyDown={(event) => {
								event.stopPropagation();
							}}
						/>
						<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
							<FontAwesomeIcon
								icon={isLoading ? faSpinner : faChevronDown}
								className={cn(
									'h-3 w-3',
									colors.classes.textSecondary,
									isLoading && 'animate-spin',
								)}
								aria-hidden="true"
							/>
						</div>
					</HeadlessCombobox.Button>
					{clearable &&
						value != null &&
						value !== '' &&
						(typeof value !== 'number' || !Number.isNaN(value)) && (
							<button
								type="button"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setSelectedOption(null);
									onChange?.('');
								}}
								className={cn(
									'absolute right-6 top-1/2 -translate-y-1/2 flex items-center justify-center z-10 cursor-pointer',
								)}
								disabled={disabled}
							>
								<FontAwesomeIcon
									icon={faTimes}
									className={cn(
										'h-3 w-3',
										colors.classes.textSecondary,
										'hover:' + colors.classes.textPrimary,
										'transition-colors',
									)}
									aria-hidden="true"
								/>
							</button>
						)}

					<Transition
						as={Fragment}
						leave="transition ease-in duration-100"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
						afterLeave={() => {
							setQuery('');
						}}
					>
						<HeadlessCombobox.Options
							className={cn(
								'absolute z-10 -mt-px max-h-60 w-full overflow-auto custom-scrollbar rounded-b-md py-1 text-base sm:text-sm',
								colors.classes.selectMenu,
							)}
						>
							{isLoading ? (
								<div
									className={cn(
										'relative cursor-default py-2 px-3 text-center',
										colors.classes.textSecondary,
									)}
								>
									<FontAwesomeIcon
										icon={faSpinner}
										className="animate-spin mr-2"
									/>
									Searching...
								</div>
							) : options.length === 0 && query !== '' ? (
								<div
									className={cn(
										'relative cursor-default py-2 px-3 text-center',
										colors.classes.textSecondary,
									)}
								>
									{emptyMessage}
								</div>
							) : (
								options.map((option) => (
									<HeadlessCombobox.Option
										key={option.value}
										value={option}
										disabled={option.disabled}
										className={({ active, selected }) =>
											cn(
												'relative py-2 pl-10 pr-4 transition-colors',
												option.disabled
													? 'cursor-not-allowed opacity-50'
													: 'cursor-pointer',
												selected &&
													!active &&
													colors.classes.selectOptionSelected,
												active && colors.classes.selectOption,
												!selected && !active && colors.classes.textPrimary,
											)
										}
									>
										{({ selected }) => (
											<>
												<div>
													<span
														className={cn(
															'block truncate',
															selected ? 'font-medium' : 'font-normal',
														)}
													>
														{option.label}
													</span>
													{option.subtitle && (
														<span
															className={cn(
																'block truncate text-sm',
																colors.classes.textSecondary,
															)}
														>
															{option.subtitle}
														</span>
													)}
												</div>
												{selected && (
													<span
														className={cn(
															'absolute inset-y-0 left-0 flex items-center pl-3',
															colors.classes.textPrimary,
														)}
													>
														<FontAwesomeIcon
															icon={faCheck}
															className="h-3 w-3"
															aria-hidden="true"
														/>
													</span>
												)}
											</>
										)}
									</HeadlessCombobox.Option>
								))
							)}
						</HeadlessCombobox.Options>
					</Transition>
				</div>
			</HeadlessCombobox>
		);
	},
);

Combobox.displayName = 'Combobox';
